/**
 * CategoryLandingRegion — "fake" lazy-reveal of Contentful region modules.
 *
 * Category Landing Pages (CMS `pageType=category`) are pure Contentful
 * pages with no SFCC product grid — `Page.mainRegionCollection` can
 * carry dozens of authored content modules (hero blocks, teaser tiles,
 * media carousels, accordions, etc). Rendering all of them at once
 * produces an extremely long page that overwhelms the shopper, which
 * the FSD explicitly calls out as a marketing problem.
 *
 * This component implements a UI-only reveal: every module past the
 * first `initialCount` (default 4) is wrapped in `<RevealOnScroll>`,
 * which starts the wrapper hidden (`opacity: 0; translateY(40px)`)
 * and transitions it to visible the moment **its own** bounding box
 * enters the viewport. All Contentful data is still fetched up front
 * in the single page-level GraphQL query — this is not a real lazy
 * fetch.
 *
 * ### Zero-height layout gate (important)
 *
 * The IntersectionObserver attached by `<RevealOnScroll>` is gated
 * on the wrapper having non-zero height. This is critical because
 * `<ContentfulComponent>` defers rendering until its own data is
 * ready (image manifest, video metadata, slot resolution etc.), and
 * during that gap the wrapper collapses to `height: 0`. Chrome's
 * IntersectionObserver treats a zero-area element whose position
 * sits within the root as **fully intersecting** (`ratio: 1`), so
 * attaching the IO too early would immediately fire `setShown(true)`
 * for *every* module past the initial chunk — including ones living
 * thousands of pixels below the viewport — and the fade animation
 * would play out before the shopper ever scrolled to it.
 *
 * We use a `ResizeObserver` to wait for the wrapper's first non-zero
 * height, then attach the IO. The IO observes a properly laid-out
 * element, so its initial intersection check correctly reports
 * `false` for modules below the fold, and the fade triggers when
 * the shopper actually scrolls them into view.
 *
 * ### Rapid navigation
 *
 * Mega-menu spam remounts category landings while IO/ResizeObserver
 * are mid-setup. Shared Contentful `sys.id` keys across pages can also
 * reuse a `<RevealOnScroll>` instance and leave `shown: false` with a
 * full-height invisible module ("ghost" gap). Callers should pass a
 * stable `pageKey` (page `sys.id`) so the region remounts per page;
 * RevealOnScroll also re-checks viewport after layout settles.
 *
 * See `docs/plp/category-landing-lazy-reveal.md` for the canonical spec.
 */

import React, {useEffect, useRef, useState} from 'react';
import PropTypes from 'prop-types';

import {Box} from '@salesforce/retail-react-app/app/components/shared/ui';

import {Component as ContentfulComponent} from '@amblique/king-living/app/components/contentful/component';
import {
  CATEGORY_LANDING_FADE_DURATION_MS,
  CATEGORY_LANDING_INITIAL_MODULE_COUNT,
  CATEGORY_LANDING_REVEAL_THRESHOLD,
} from '@amblique/king-living/app/constants';

/**
 * True when the node's box overlaps the viewport (any amount).
 * Used as a sync fallback when IntersectionObserver misses the first
 * paint after rapid route changes.
 *
 * @param {Element} node
 * @returns {boolean}
 */
const isOverlappingViewport = (node) => {
  const rect = node.getBoundingClientRect();

  if (rect.height <= 0 || rect.width <= 0) {
    return false;
  }

  const viewportHeight = window.innerHeight || document.documentElement.clientHeight;

  return rect.top < viewportHeight && rect.bottom > 0;
};

/**
 * Wraps a single Contentful module. Renders the module immediately
 * (data fetching / image preload begins right away) but keeps the
 * wrapper at `opacity: 0; translateY(40px)` until **its own**
 * IntersectionObserver reports it has crossed `threshold` into the
 * viewport. At that point a CSS transition fades it to its natural
 * position, so the shopper actually witnesses the animation in
 * their eye-line.
 *
 * @param {object} props
 * @param {React.ReactNode} props.children
 * @param {number} props.durationMs - fade transition duration in ms
 * @param {number} [props.threshold] - IntersectionObserver threshold
 */
const RevealOnScroll = ({
  children,
  durationMs,
  threshold = CATEGORY_LANDING_REVEAL_THRESHOLD,
  ...rest
}) => {
  const ref = useRef(null);
  const [shown, setShown] = useState(false);

  useEffect(() => {
    if (shown) return undefined;

    const node = ref.current;

    if (!node) return undefined;

    // Bail out gracefully if the browser is too old. We'd rather show
    // the module than hide it forever.
    if (typeof IntersectionObserver === 'undefined') {
      setShown(true);

      return undefined;
    }

    let intersectionObs = null;
    let resizeObs = null;
    let rafId = 0;
    let didReveal = false;

    const reveal = () => {
      if (didReveal) return;
      didReveal = true;
      setShown(true);
      if (intersectionObs) {
        intersectionObs.disconnect();
        intersectionObs = null;
      }
      if (resizeObs) {
        resizeObs.disconnect();
        resizeObs = null;
      }
      if (rafId) {
        cancelAnimationFrame(rafId);
        rafId = 0;
      }
    };

    const attachIntersection = () => {
      if (didReveal || intersectionObs) return;

      // Sync path: after rapid nav the module may already sit in view
      // before IO delivers its first callback.
      if (isOverlappingViewport(node)) {
        reveal();

        return;
      }

      intersectionObs = new IntersectionObserver(
        ([entry]) => {
          // Guard against the "0-height intersecting" trap: Chrome
          // reports a zero-area target whose position is inside the
          // root as fully intersecting (`ratio: 1`). If the wrapper
          // briefly collapses mid-render we must not treat that as
          // a real viewport entry.
          if (
            entry.isIntersecting &&
            entry.boundingClientRect.height > 0
          ) {
            reveal();
          }
        },
        {threshold}
      );

      intersectionObs.observe(node);

      // One more check after layout settles (images / nested fetch).
      rafId = requestAnimationFrame(() => {
        rafId = requestAnimationFrame(() => {
          if (!didReveal && isOverlappingViewport(node)) {
            reveal();
          }
        });
      });
    };

    // If the wrapper already has real height (eg. SSR'd module that
    // hydrated quickly, or HMR re-mount), attach the IO directly.
    // Otherwise wait for ResizeObserver to report the first non-zero
    // height — that's the moment the layout actually means something.
    if (node.getBoundingClientRect().height > 0) {
      attachIntersection();
    } else if (typeof ResizeObserver !== 'undefined') {
      resizeObs = new ResizeObserver((entries) => {
        for (const entry of entries) {
          if (entry.contentRect.height > 0) {
            resizeObs.disconnect();
            resizeObs = null;
            attachIntersection();
            break;
          }
        }
      });
      resizeObs.observe(node);
    } else {
      // No ResizeObserver — attach IO and rely on the height guard
      // inside the callback to ignore zero-area fires.
      attachIntersection();
    }

    return () => {
      if (intersectionObs) intersectionObs.disconnect();
      if (resizeObs) resizeObs.disconnect();
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, [shown, threshold]);

  return (
    <Box
      ref={ref}
      sx={{
        opacity: shown ? 1 : 0,
        pointerEvents: shown ? 'auto' : 'none',
        transform: shown ? 'none' : 'translateY(40px)',
        transition: `opacity ${durationMs}ms ease-out, transform ${durationMs}ms ease-out`,
        willChange: shown ? 'auto' : 'opacity, transform',
      }}
      {...rest}
    >
      {children}
    </Box>
  );
};

RevealOnScroll.propTypes = {
  children: PropTypes.node.isRequired,
  durationMs: PropTypes.number.isRequired,
  threshold: PropTypes.number,
};

/**
 * @param {object} props
 * @param {Array<object>} props.items - filtered `mainRegionCollection.items`
 * @param {number} [props.initialCount=4] - modules rendered as-is (no fade) on first paint
 * @param {number} [props.fadeDurationMs] - per-module fade transition duration in ms
 * @param {string} [props.pageKey] - Contentful page id; remounts reveal wrappers per page
 * @returns {React.ReactNode}
 */
export const CategoryLandingRegion = ({
  items,
  initialCount = CATEGORY_LANDING_INITIAL_MODULE_COUNT,
  fadeDurationMs = CATEGORY_LANDING_FADE_DURATION_MS,
  pageKey,
}) => {
  const total = items.length;
  const safeInitial = Math.max(0, Math.min(initialCount, total));

  return (
    <Box className="category-landing-region" data-category-landing-page={pageKey || undefined}>
      {items.map((component, index) => {
        // Modules in the initial chunk render immediately with no
        // animation — they're above the fold and the shopper expects
        // them on first paint. Every other module goes through
        // <RevealOnScroll> so its fade animation runs when the
        // shopper actually scrolls to it, not when React mounts it.
        //
        // Key includes pageKey so shared Contentful entry ids across
        // category landings do not reuse a stuck `shown: false` instance
        // after rapid mega-menu navigation.
        const moduleKey = pageKey ? `${pageKey}:${component.sys.id}` : component.sys.id;

        if (index < safeInitial) {
          return (
            <Box
              key={moduleKey}
              data-category-landing-module-index={index}
              className="category-landing-region__module"
            >
              <ContentfulComponent component={component} />
            </Box>
          );
        }

        return (
          <RevealOnScroll
            key={moduleKey}
            data-category-landing-module-index={index}
            durationMs={fadeDurationMs}
            className="category-landing-region__module"
          >
            <ContentfulComponent component={component} />
          </RevealOnScroll>
        );
      })}
    </Box>
  );
};

CategoryLandingRegion.displayName = 'CategoryLandingRegion';
CategoryLandingRegion.propTypes = {
  items: PropTypes.arrayOf(PropTypes.object).isRequired,
  initialCount: PropTypes.number,
  fadeDurationMs: PropTypes.number,
  pageKey: PropTypes.string,
};

export default CategoryLandingRegion;
